import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { useAuth } from '@/core/auth';
import { ONBOARDING_ROUTE, isOnboardingRoute, useOnboarding } from '@/core/onboarding';
import { hasPermission } from './permissions';
import type { PermissionAction } from './types';

interface RequireAuthProps {
  /**
   * Optional route key checked against the current user's permission menu.
   * When provided, a logged-in user without the required permission sees a
   * 403 message instead of the protected content.
   */
  route?: string;
  /** Action to check when `route` is provided. */
  action?: PermissionAction;
  children: ReactNode;
}

/**
 * Route wrapper that:
 *  1. Redirects unauthenticated users to /login (the login route is added in a
 *     future slice — the path is kept correct here so wiring works as-is).
 *  2. Optionally checks a specific menu permission and renders a localised 403
 *     message when the authenticated user lacks the required action.
 */
export function RequireAuth({ route, action, children }: RequireAuthProps): ReactNode {
  const { isAuthenticated, currentUser } = useAuth();
  const { status, loading: onboardingLoading } = useOnboarding();
  const location = useLocation();
  const { pathname } = location;
  const { t } = useTranslation();

  if (!isAuthenticated) {
    // Remember where they were going. Emails link straight to a tow request or
    // a claim, and landing on the dashboard after signing in lost the link.
    return <Navigate to="/login" replace state={{ from: `${pathname}${location.search}${location.hash}` }} />;
  }

  /**
   * The onboarding gate.
   *
   * Here rather than on each route because this wrapper is the one thing every
   * protected page already passes through — a per-page check is a list that
   * someone eventually forgets to add a page to, and the page they forget is
   * the hole.
   *
   * Three deliberate exemptions from redirecting:
   *  - while the status is still loading, or nothing came back at all. The
   *    provider fails open, and holding the whole app behind a request that
   *    might never answer is worse than the thing being guarded.
   *  - the wizard itself and the pages it sends people to, or the
   *    redirect is a loop.
   *  - anyone the server does not say is `blocked`, which is every admin,
   *    adjuster and driver, a finished tenant, and an approved tenant who has
   *    since gone incomplete — that last one keeps working and gets a banner
   *    instead. The roles that are exempt are the server's business, not a
   *    list maintained here.
   */
  const mustOnboard =
    !onboardingLoading &&
    status !== null &&
    status.blocked &&
    !isOnboardingRoute(pathname);

  if (mustOnboard) {
    return <Navigate to={ONBOARDING_ROUTE} replace />;
  }

  if (route && action !== undefined && currentUser) {
    const allowed = hasPermission(currentUser.menu, route, action);
    if (!allowed) {
      return (
        <Typography variant="body1" color="error">
          {t('errors.forbidden')}
        </Typography>
      );
    }
  }

  return <>{children}</>;
}
